import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './LandingPage5.module.css';

import sec1card1 from '../../../../Images/newLandingPage/sec1card1.png';
import sec1card2 from '../../../../Images/newLandingPage/sec1card2.png';
import sec1card3 from '../../../../Images/newLandingPage/sec1card3.png';
import sec1card4 from '../../../../Images/newLandingPage/sec1card4.png';

gsap.registerPlugin(ScrollTrigger);

const LandingPage5 = () => {
  const flowRef = useRef(null);

  const steps = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.57a1 1 0 0 0-1.01.24l-2.2 2.2a15.045 15.045 0 0 1-6.59-6.59l2.2-2.2a1 1 0 0 0 .24-1.01 11.285 11.285 0 0 1-.57-3.53c0-.55-.45-1-1-1H3.03c-.55 0-1 .45-1 1C2.03 16.54 13.46 28 27.03 28c.55 0 1-.45 1-1v-4.01c0-.55-.45-1-1-1z" fill="currentColor" />
        </svg>
      ),
      title: "Call Answering",
      desc: "Human-like voice pickup within one ring."
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="currentColor" />
        </svg>
      ),
      title: "Qualifies & Captures",
      desc: "Every intent captured and scored in real time."
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor" />
        </svg>
      ),
      title: "Logs to CRM",
      desc: "Instant sync to Salesforce, HubSpot, and more."
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor" />
        </svg>
      ),
      title: "Your Team Gets a Lead",
      desc: "Warm handoff with full context to close faster."
    }
  ];

  const useCases = [
    {
      tag: "Sales teams",
      desc: "Qualify inbound faster.",
      img: sec1card1,
      rotateClass: styles.rotateCard1
    },
    {
      tag: "Customer support",
      desc: "Deflect 80% of tickets.",
      img: sec1card2,
      rotateClass: styles.rotateCard2
    },
    {
      tag: "Operations",
      desc: "Automate scheduling & ops.",
      img: sec1card3,
      rotateClass: styles.rotateCard3
    },
    {
      tag: "Enterprise",
      desc: "Compliance-ready scale.",
      img: sec1card4,
      rotateClass: styles.rotateCard4
    }
  ];

  useEffect(() => {
    const cards = flowRef.current.querySelectorAll(`.${styles.flowCard}`);
    if (cards.length >= 4) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: flowRef.current,
          start: "top 80%"
        }
      });
      // Stagger order: Card 1, then Card 3, then Card 2, then Card 4
      tl.fromTo(cards[0], { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" })
        .fromTo(cards[2], { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, "-=0.35")
        .fromTo(cards[1], { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, "-=0.35")
        .fromTo(cards[3], { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, "-=0.35");
    }
  }, []);

  return (
    <section className={styles.section}>
      {/* Connected Flow Banner */}
      <div className={styles.flowBanner}>
        <div className={styles.container}>
          <h2 className={styles.flowTitle}>
            How ToxBox AI converts calls <br />
            <span className={styles.gradientText}>into customers.</span>
          </h2>

          <div className={styles.flowGrid} ref={flowRef}>
            {steps.map((step, idx) => (
              <div key={idx} className={styles.flowCard}>
                <div className={styles.flowIcon}>{step.icon}</div>
                <h3 className={styles.flowCardTitle}>{step.title}</h3>
                <p className={styles.flowCardDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Built for Teams Section */}
      <div className={styles.teamsSection}>
        <div className={styles.container}>
          <div className={styles.teamsGrid}>
            {/* Left text */}
            <div className={styles.teamsLeft}>
              <span className={styles.purpleTag}>USE CASES</span>
              <h2 className={styles.teamsTitle}>
                Built for teams <br />
                that <span className={styles.gradientText}>live on the phone.</span>
              </h2>
              
              <ul className={styles.checkList}>
                <li>
                  <span className={styles.checkIcon}>✓</span>
                  Instant deployment
                </li>
                <li>
                  <span className={styles.checkIcon}>✓</span>
                  No engineering required
                </li>
                <li>
                  <span className={styles.checkIcon}>✓</span>
                  Native CRM integrations
                </li>
                <li>
                  <span className={styles.checkIcon}>✓</span>
                  Multilingual by default
                </li>
              </ul>
            </div>

            {/* Right Use Case Cards Grid with clockwise rotation entry */}
            <div className={styles.useCaseGridContainer}>
              <div className={styles.useCaseGrid}>
                {useCases.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`${styles.useCaseCard} ${item.rotateClass}`} 
                    style={{ backgroundImage: `url(${item.img})` }}
                  >
                    <div className={styles.useCaseOverlay}></div>
                    <div className={styles.useCaseContent}>
                      <h3 className={styles.useCaseTag}>{item.tag}</h3>
                      <p className={styles.useCaseDesc}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingPage5;
